import { useEffect, useMemo, useState } from 'react';
import type { FormField, FieldType, Locator, PathHop } from '@/features/application/types/forms';
import type { CrmField } from '@/features/form-builder/types';
import { useMappingsStore } from '@/features/form-builder/store/mappings.store';
import { useCrmStore } from '@/features/form-builder/store/crm.store';
import type { BankAnalysisMetricSelection } from '@/features/form-builder/store/bank-analysis-mapping.store';
import { useBankAnalysisMappingStore } from '@/features/form-builder/store/bank-analysis-mapping.store';
import {
  createFieldMappingUpdate,
  isFormTypeCrmTypeCompatible,
  validateMaxLength,
  getCompatibleFormTypes,
  validateLeadingZeroRisk,
} from '@/features/form-builder/utils/field-mapping-validation.utils';
import { generateMappingSummary } from '@/features/form-builder/utils/mapping-summary.utils';

type ActiveStep = 'path' | 'field';

interface TypeChangeModalState {
  isOpen: boolean;
  compatibleTypes: FieldType[];
  selectedCrmField: CrmField | null;
}

interface DuplicateModalState {
  isOpen: boolean;
  conflictingFields: Array<Pick<FormField, 'id' | 'label'>>;
  crmFieldLabel: string;
}

interface LeadingZeroWarningModalState {
  isOpen: boolean;
  onConfirm: () => void;
  formFieldLabel: string;
  crmFieldLabel: string;
  crmFieldType: string;
}

interface MaxLengthModalState {
  isOpen: boolean;
  validationError: {
    formFieldMaxLength: number;
    crmFieldLength: number;
    fieldLabel: string;
    crmFieldLabel: string;
  } | null;
}

const areLocatorsEqual = (
  locator1: Locator | null | undefined,
  locator2: Locator | null | undefined
): boolean => {
  if (locator1 === locator2) return true;
  if (!locator1 && !locator2) return true;
  if (!locator1 || !locator2) return false;

  if (locator1.strategy !== locator2.strategy) return false;

  const params1 = locator1.params || {};
  const params2 = locator2.params || {};
  const keys1 = Object.keys(params1);
  const keys2 = Object.keys(params2);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (params1[key] !== params2[key]) return false;
  }

  return true;
};

// Purpose: Compare two PathHop arrays including locators.
const arePathsEqual = (path1: PathHop[] | undefined, path2: PathHop[] | undefined): boolean => {
  if (!path1 && !path2) return true;
  if (!path1 || !path2 || path1.length !== path2.length) return false;

  return path1.every((hop1, idx) => {
    const hop2 = path2[idx];
    const locatorsEqual = areLocatorsEqual(hop1.locator, hop2.locator);

    return (
      hop1.object === hop2.object &&
      hop1.rel === hop2.rel &&
      hop1.kind === hop2.kind &&
      locatorsEqual
    );
  });
};

// Purpose: CRM mapping workflow for bank analysis metric fields (stored outside form_structure).
export const useBankAnalysisCrmMapping = (
  field: FormField,
  selection: BankAnalysisMetricSelection,
  isActive?: boolean
) => {
  const { getCurrentPrimaryObject } = useMappingsStore();
  const { updateMetricField, metricFieldsByKey } = useBankAnalysisMappingStore();
  const { getFieldsForObject, isLoadingFieldsForObject, fetchCrmObjectRelationships } = useCrmStore();

  const primaryObject = getCurrentPrimaryObject();

  const targetObjectType = useMemo(() => {
    if (!field.path || field.path.length === 0) return primaryObject;
    const lastHop = field.path[field.path.length - 1];
    return lastHop.object;
  }, [field.path, primaryObject]);

  const [activeStep, setActiveStep] = useState<ActiveStep | null>(() => {
    if (field.fieldApiName) return null;
    if ((field as any).isPathConfirmed) return 'field';
    return 'path';
  });

  const [selectedFieldApiName, setSelectedFieldApiName] = useState(field.fieldApiName || '');
  const [isFieldComboboxOpen, setIsFieldComboboxOpen] = useState(false);

  const [typeChangeModal, setTypeChangeModal] = useState<TypeChangeModalState>({
    isOpen: false,
    compatibleTypes: [],
    selectedCrmField: null,
  });

  const [duplicateModal, setDuplicateModal] = useState<DuplicateModalState>({
    isOpen: false,
    conflictingFields: [],
    crmFieldLabel: '',
  });

  const [leadingZeroWarningModal, setLeadingZeroWarningModal] = useState<LeadingZeroWarningModalState>({
    isOpen: false,
    onConfirm: () => {},
    formFieldLabel: '',
    crmFieldLabel: '',
    crmFieldType: '',
  });

  const [maxLengthModal, setMaxLengthModal] = useState<MaxLengthModalState>({
    isOpen: false,
    validationError: null,
  });

  // Modal state for missing locator when using child relationships
  const [locatorRequiredModalOpen, setLocatorRequiredModalOpen] = useState(false);

  const isFieldComplete = !!field.fieldApiName;
  const isMapped = !!field.fieldApiName;

  const getMappingSummary = () => generateMappingSummary(field);

  useEffect(() => {
    if (targetObjectType) {
      fetchCrmObjectRelationships(targetObjectType);
    }
  }, [targetObjectType, fetchCrmObjectRelationships]);

  // Ensure path always starts with anchor hop
  useEffect(() => {
    if (primaryObject && (!field.path || field.path.length === 0)) {
      const anchorHop: PathHop = { object: primaryObject, kind: 'anchor' };
      updateMetricField(selection, { path: [anchorHop] } as any);
    }
  }, [primaryObject, field.path, selection, updateMetricField]);

  // Reset steps when this metric becomes active
  useEffect(() => {
    if (isActive && !field.fieldApiName) {
      if (!(field as any).isPathConfirmed) setActiveStep('path');
      else setActiveStep('field');
    }
  }, [isActive, field.fieldApiName, (field as any).isPathConfirmed]);

  useEffect(() => {
    if (field.fieldApiName) setSelectedFieldApiName(field.fieldApiName);
    else setSelectedFieldApiName('');
  }, [field.fieldApiName]);

  const availableFields = targetObjectType ? getFieldsForObject(targetObjectType) : [];
  const isLoading = targetObjectType ? isLoadingFieldsForObject(targetObjectType) : false;

  const handleConfirmStep = (step: ActiveStep) => {
    if (step === 'path') {
      updateMetricField(selection, { isPathConfirmed: true } as any);
      setActiveStep('field');
      setIsFieldComboboxOpen(true);
    }
  };

  const handlePathChange = (newPath: PathHop[]) => {
    updateMetricField(selection, { path: newPath } as any);
  };

  const checkForDuplicateMetricMapping = (newPath: PathHop[] | undefined, newApiName: string) => {
    const all = Object.values(metricFieldsByKey);
    return all
      .filter((f: any) => {
        const isSameMetric = f.metricId === selection.metricId;
        const sameSourceIndex = (f.sourceIndex ?? undefined) === (selection.sourceIndex ?? undefined);
        return !(isSameMetric && sameSourceIndex);
      })
      .filter((f) => f.fieldApiName && f.fieldApiName === newApiName)
      .filter((f) => arePathsEqual(f.path, newPath))
      .map((f) => ({ id: f.id, label: f.label }));
  };

  const handleConfirmMapping = async () => {
    if (!selectedFieldApiName) return;
    const selectedField = availableFields?.find((f: CrmField) => f.id === selectedFieldApiName);
    if (!selectedField) return;

    // Purpose: Ensure all child hops have locators before confirming.
    for (const hop of field.path || []) {
      if (hop.kind === 'child' && !hop.locator) {
        setLocatorRequiredModalOpen(true);
        return;
      }
    }

    const proceedWithMapping = () => {
      const conflicts = checkForDuplicateMetricMapping(field.path, selectedFieldApiName);
      if (conflicts.length > 0) {
        setDuplicateModal({ isOpen: true, conflictingFields: conflicts, crmFieldLabel: selectedField.label });
        return;
      }

      if (!isFormTypeCrmTypeCompatible(field.type, selectedField.type)) {
        const compatibleTypes = getCompatibleFormTypes(selectedField.type);
        setTypeChangeModal({ isOpen: true, compatibleTypes, selectedCrmField: selectedField });
        return;
      }

      if ((field as any).formFieldMaxLength && selectedField.length) {
        const maxLengthCheck = validateMaxLength(field as any, selectedField);
        if (maxLengthCheck) {
          setMaxLengthModal({
            isOpen: true,
            validationError: {
              formFieldMaxLength: maxLengthCheck.formFieldMaxLength,
              crmFieldLength: maxLengthCheck.crmFieldLength,
              fieldLabel: field.label,
              crmFieldLabel: selectedField.label,
            },
          });
          return;
        }
      }

      applyCrmMapping(selectedField, field.type);
    };

    if (validateLeadingZeroRisk(field, selectedField.type)) {
      setLeadingZeroWarningModal({
        isOpen: true,
        onConfirm: () => {
          handleLeadingZeroWarningClose();
          proceedWithMapping();
        },
        formFieldLabel: field.label,
        crmFieldLabel: selectedField.label,
        crmFieldType: selectedField.type,
      });
    } else {
      proceedWithMapping();
    }
  };

  const applyCrmMapping = (selectedField: CrmField, newFieldType: FieldType) => {
    const mappingUpdate = createFieldMappingUpdate(field as any, selectedField, newFieldType);
    updateMetricField(selection, {
      ...(mappingUpdate as any),
      fieldApiName: selectedFieldApiName,
      fieldLabel: selectedField.label,
      isPathConfirmed: true,
    } as any);
    setActiveStep(null);
  };

  const handleTypeChangeModalClose = () => {
    setTypeChangeModal({ isOpen: false, compatibleTypes: [], selectedCrmField: null });
  };

  const handleTypeChangeModalConfirm = (newType: FieldType) => {
    if (typeChangeModal.selectedCrmField) {
      applyCrmMapping(typeChangeModal.selectedCrmField, newType);
    }
    handleTypeChangeModalClose();
  };

  const handleDuplicateModalClose = () => {
    setDuplicateModal({ isOpen: false, conflictingFields: [], crmFieldLabel: '' });
  };

  const handleMaxLengthModalClose = () => {
    setMaxLengthModal({ isOpen: false, validationError: null });
  };

  const handleLeadingZeroWarningClose = () => {
    setLeadingZeroWarningModal({
      isOpen: false,
      onConfirm: () => {},
      formFieldLabel: '',
      crmFieldLabel: '',
      crmFieldType: '',
    });
  };

  return {
    activeStep,
    setActiveStep,
    selectedFieldApiName,
    setSelectedFieldApiName,
    availableFields,
    isLoading,
    isFieldComplete,
    isFieldComboboxOpen,
    setIsFieldComboboxOpen,
    isMapped,
    getMappingSummary,
    typeChangeModal,
    duplicateModal,
    maxLengthModal,
    leadingZeroWarningModal,
    locatorRequiredModalOpen,
    setLocatorRequiredModalOpen,
    handleConfirmStep,
    handlePathChange,
    handleConfirmMapping,
    handleTypeChangeModalClose,
    handleTypeChangeModalConfirm,
    handleDuplicateModalClose,
    handleMaxLengthModalClose,
    handleLeadingZeroWarningClose,
  };
};
